/**
 * features/selfservice/MyPayHr.tsx
 * ------------------------------------------------------------------
 * "My Pay & HR" — the staff self-service view. A linked staff member
 * sees ONLY their own:
 *   • Payslips   — history + download PDF (re-rendered from the slip)
 *   • Leave      — balance, request history, and apply for leave
 *   • Tax        — their own ITW-8 certificate for a chosen tax year
 *
 * Read access is scoped by linkedStaffId in the Firestore rules; this
 * screen never loads anyone else's data.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  IdCard, ReceiptText, CalendarDays, FileText, Download, Plus, AlertTriangle, X, Check,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useMyStaffData } from '@/features/selfservice/useMyStaffData';
import { hr } from '@/features/hr/hrWrite';
import { LEAVE_TYPE_LABELS, type LeaveType, type LeaveStatus } from '@/lib/hr';
import { currentLeaveYear, leaveBalancesFor } from '@/lib/leavePolicy';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { fmtPula } from '@/lib/hrPayroll';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildPayslipPdf } from '@/lib/payslipPdf';
import { periodLabel } from '@/lib/assemblePayslip';
import {
  buildAnnualByEmployee, resolveEmployerDetails, taxYear, taxYearEndYearOf,
} from '@/lib/bursForms';
import { buildItw8Pdf } from '@/lib/bursPdf';
import { toEncashmentLike } from '@/lib/leaveEncashment';

type Tab = 'payslips' | 'leave' | 'tax';

const STATUS_TONE: Record<LeaveStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelled: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export default function MyPayHr() {
  const { userData } = useAuth();
  const staffId = userData?.linkedStaffId;
  const data = useMyStaffData(staffId);
  const [tab, setTab] = useState<Tab>('payslips');
  const [logo, setLogo] = useState<string | null>(null);
  useEffect(() => { loadLogoDataUrl().then(setLogo); }, []);

  if (!staffId) {
    return (
      <div className="space-y-6">
        <GradientHero icon={IdCard} title="My Pay & HR" subtitle="Payslips · leave · tax certificate" />
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} /> Your account isn't linked to a staff record yet. Ask an administrator to link it (User Management → your account → Link to staff record).
          </div>
        </SoftCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={IdCard}
        title="My Pay & HR"
        subtitle={data.staff ? `${data.staff.firstName} ${data.staff.lastName} · ${data.staff.jobTitle}` : 'Payslips · leave · tax certificate'}
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        {([['payslips', 'Payslips', ReceiptText], ['leave', 'Leave', CalendarDays], ['tax', 'Tax certificate', FileText]] as const).map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === id
                ? 'border-[var(--accent-strong,#1b3a6b)] text-[var(--accent-link,#2d5295)] dark:text-[var(--accent-link-dark,#93c5fd)]'
                : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
            }`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {data.loading ? (
        <div className="flex justify-center py-16"><Spinner label="Loading…" /></div>
      ) : tab === 'payslips' ? (
        <PayslipsTab data={data} logo={logo} />
      ) : tab === 'leave' ? (
        <LeaveTab data={data} staffId={staffId} />
      ) : (
        <TaxTab data={data} logo={logo} />
      )}
    </div>
  );
}

/* ---------------- Payslips ---------------- */
function PayslipsTab({ data, logo }: { data: ReturnType<typeof useMyStaffData>; logo: string | null }) {
  const slips = useMemo(
    () => [...data.payslips].sort((a, b) => (b.period.year - a.period.year) || (b.period.month - a.period.month)),
    [data.payslips],
  );
  function download(slip: (typeof slips)[number]) {
    buildPayslipPdf(slip, { logoDataUrl: logo }).save(`payslip_${slip.employeeNo}_${slip.period.year}-${String(slip.period.month).padStart(2, '0')}.pdf`);
  }
  if (slips.length === 0) {
    return <SoftCard hover={false}><p className="text-sm text-gray-500 dark:text-gray-400">No payslips yet.</p></SoftCard>;
  }
  return (
    <SoftCard pad="p-0" hover={false}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 px-4 font-medium">Period</th>
              <th className="py-2 px-4 font-medium text-right">Gross</th>
              <th className="py-2 px-4 font-medium text-right">PAYE</th>
              <th className="py-2 px-4 font-medium text-right">Net</th>
              <th className="py-2 px-4 font-medium text-right">Payslip</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {slips.map((s) => (
              <tr key={s.id}>
                <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{periodLabel(s.period)}</td>
                <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(s.earningsTotal)}</td>
                <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(s.paye)}</td>
                <td className="py-2 px-4 text-right font-medium text-gray-900 dark:text-white">{fmtPula(s.net)}</td>
                <td className="py-2 px-4 text-right">
                  <button onClick={() => download(s)} className="inline-flex items-center gap-1 text-[var(--accent-link,#2d5295)] hover:underline">
                    <Download size={14} /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SoftCard>
  );
}

/* ---------------- Leave ---------------- */
function LeaveTab({ data, staffId }: { data: ReturnType<typeof useMyStaffData>; staffId: string }) {
  // The figures HR sees: allocation over cohort policy over school default (Oct 2026).
  const { policies } = useLeavePolicies();
  const balances = useMemo(
    () => leaveBalancesFor({ category: data.staff?.category, allocation: data.allocation, requests: data.leave, policies, year: currentLeaveYear(), alwaysShow: ['annual', 'sick'], employmentStart: data.staff?.startDate, encashments: toEncashmentLike(data.encashments) }),
    [data.allocation, data.leave, data.staff, data.encashments, policies],
  );
  const history = useMemo(
    () => [...data.leave].sort((a, b) => (b.startDate || '').localeCompare(a.startDate || '')),
    [data.leave],
  );
  const [applying, setApplying] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {balances.map((b) => (
            <StatChip
              key={b.type}
              tone={b.remaining <= 0 ? 'red' : 'emerald'}
              value={`${b.remaining} d`}
              label={`${LEAVE_TYPE_LABELS[b.type as LeaveType] ?? b.type} left`}
            />
          ))}
        </div>
        <button onClick={() => setApplying((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
          <Plus size={15} /> Apply for leave
        </button>
      </div>

      {applying && <ApplyLeaveForm staffId={staffId} onClose={() => setApplying(false)} />}

      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">My leave requests</h3>
        </div>
        {history.length === 0 ? (
          <p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">No leave requests yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-4 font-medium">Type</th>
                  <th className="py-2 px-4 font-medium">From</th>
                  <th className="py-2 px-4 font-medium">To</th>
                  <th className="py-2 px-4 font-medium text-right">Days</th>
                  <th className="py-2 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {history.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{LEAVE_TYPE_LABELS[l.type] ?? l.type}</td>
                    <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{new Date(l.startDate).toLocaleDateString()}</td>
                    <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{new Date(l.endDate).toLocaleDateString()}</td>
                    <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{l.days}</td>
                    <td className="py-2 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[l.status]}`}>{l.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}

function ApplyLeaveForm({ staffId, onClose }: { staffId: string; onClose: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [save, setSave] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const days = useMemo(() => {
    const s = new Date(startDate).getTime();
    const e = new Date(endDate).getTime();
    if (Number.isNaN(s) || Number.isNaN(e)) return 0;
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  }, [startDate, endDate]);
  const canSave = !!startDate && !!endDate && days > 0;

  async function submit() {
    if (!canSave) return;
    setSave({ show: true, status: 'saving', message: 'Submitting leave request' });
    try {
      await hr.leave.add({
        staffId,
        type,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        days,
        reason: reason.trim(),
        status: 'pending',
      });
      setSave({ show: true, status: 'success', message: 'Leave request submitted for approval.' });
      onClose();
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Could not submit.' });
    }
  }

  return (
    <SoftCard hover={false}>
      <SaveConfirmation show={save.show} status={save.status} message={save.message} onDismiss={() => setSave((s) => ({ ...s, show: false }))} />
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Apply for leave</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value as LeaveType)} className={glassFieldClass('mt-1 w-full')}>
            {(Object.keys(LEAVE_TYPE_LABELS) as LeaveType[]).map((t) => (
              <option key={t} value={t}>{LEAVE_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Days</span>
          <input value={`${days}`} readOnly className={glassFieldClass('mt-1 w-full bg-gray-50 dark:bg-gray-800/60')} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">From</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={glassFieldClass('mt-1 w-full')} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">To</span>
          <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className={glassFieldClass('mt-1 w-full')} />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Reason (optional)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={glassFieldClass('mt-1 w-full')} />
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">Cancel</button>
        <button onClick={submit} disabled={!canSave} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl disabled:opacity-50">
          <Check size={15} /> Submit request
        </button>
      </div>
    </SoftCard>
  );
}

/* ---------------- Tax certificate (own ITW-8) ---------------- */
function TaxTab({ data, logo }: { data: ReturnType<typeof useMyStaffData>; logo: string | null }) {
  const defaultEnd = taxYearEndYearOf({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [endYear, setEndYear] = useState(defaultEnd);
  const ty = taxYear(endYear);
  const row = useMemo(() => buildAnnualByEmployee(data.payslips, endYear)[0], [data.payslips, endYear]);
  const emp = resolveEmployerDetails(undefined); // self can't read payroll settings; SCHOOL fallback (TIN blank)

  function download() {
    if (!row) return;
    buildItw8Pdf([row], ty, emp, { logoDataUrl: logo }).save(`ITW-8_${row.employeeNo}_${endYear}.pdf`);
  }

  const yearOptions = [defaultEnd + 1, defaultEnd, defaultEnd - 1, defaultEnd - 2];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{ty.label} · your employee tax deduction certificate (ITW-8)</p>
        <select value={endYear} onChange={(e) => setEndYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
          {yearOptions.map((y) => <option key={y} value={y}>{`FY ending 30 Jun ${y}`}</option>)}
        </select>
      </div>
      {!row ? (
        <SoftCard hover={false}><p className="text-sm text-gray-500 dark:text-gray-400">No payslips in {ty.label}, so there's nothing to certify for that year.</p></SoftCard>
      ) : (
        <SoftCard hover={false}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <StatChip tone="slate" value={String(row.monthsPaid)} label="months paid" />
              <StatChip tone="blue" value={`P${fmtPula(row.gross)}`} label="gross emoluments" />
              <StatChip tone="emerald" value={`P${fmtPula(row.totalTax)}`} label="total tax deducted" />
            </div>
            <button onClick={download} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
              <FileText size={15} /> Download ITW-8
            </button>
          </div>
        </SoftCard>
      )}
    </div>
  );
}
